import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAraAuth } from '../contexts/AraAuthContext';
import { useAraTheme } from '../contexts/AraThemeContext';
import { LockKey, Eye, EyeSlash, ArrowLeft } from 'phosphor-react';
import { toast } from 'sonner';

const AraStart = () => {
  const [passcode, setPasscode] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [understood, setUnderstood] = useState(false);
  const [busy, setBusy] = useState(false);
  const { createPasscode, hasPasscode, isAuthenticated, isLoading } = useAraAuth();
  const { theme } = useAraTheme();
  const navigate = useNavigate();
  const logo = theme === 'dark' ? '/ara-logo-dark.png' : '/ara-logo-light.png';

  // Only redirect to unlock if a passcode exists AND we haven't just created
  // one (i.e. we're not authenticated yet). Prevents the post-create race
  // that would send a newly-locked user to /ara/unlock instead of onboarding.
  if (!isLoading && hasPasscode && !isAuthenticated) {
    navigate('/ara/unlock', { replace: true });
    return null;
  }

  const submit = async (e) => {
    e.preventDefault();
    if (passcode.length < 4) {
      toast.error('Please choose at least 4 characters');
      return;
    }
    if (passcode !== confirm) {
      toast.error('Passcodes do not match');
      return;
    }
    if (!understood) {
      toast.error('Please confirm you understand recovery is not possible');
      return;
    }
    setBusy(true);
    try {
      await createPasscode(passcode);
      navigate('/ara/onboarding');
    } catch (err) {
      toast.error('Could not create your Ara lock');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ara-root min-h-screen flex flex-col" data-ara-theme={theme} data-testid="ara-start">
      <header className="max-w-3xl mx-auto w-full px-6 py-4">
        <button
          onClick={() => navigate('/ara')}
          className="inline-flex items-center gap-2 ara-text-muted hover:ara-text transition-colors text-sm"
          data-testid="ara-start-back"
        >
          <ArrowLeft size={16} /> Back
        </button>
      </header>
      <main className="flex-1 flex items-center justify-center px-6 pb-16">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center mb-6">
            <img src={logo} alt="Ara" className="w-20 h-20 rounded-full mb-4 ara-logo-glow" />
            <div className="ara-chip rounded-full p-3 mb-3">
              <LockKey size={22} weight="duotone" />
            </div>
            <h1 className="text-3xl font-serif font-semibold ara-text" data-testid="ara-start-title">Create your Ara lock</h1>
            <p className="text-sm ara-text-muted text-center mt-2">
              Your Ara sanctuary is separate from Vela, encrypted on this device.
            </p>
          </div>

          <form onSubmit={submit} className="ara-card rounded-3xl p-6 space-y-5">
            <div>
              <label className="block text-sm font-medium ara-text mb-2">Choose a passcode</label>
              <div className="relative">
                <input
                  type={show ? 'text' : 'password'}
                  value={passcode}
                  onChange={(e) => setPasscode(e.target.value)}
                  className="ara-input w-full rounded-xl px-4 py-3"
                  placeholder="Enter passcode"
                  data-testid="ara-passcode-input"
                  autoFocus
                  required
                />
                <button
                  type="button"
                  onClick={() => setShow(!show)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 ara-text-muted"
                >
                  {show ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium ara-text mb-2">Confirm passcode</label>
              <input
                type={show ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="ara-input w-full rounded-xl px-4 py-3"
                placeholder="Confirm passcode"
                data-testid="ara-confirm-input"
                required
              />
            </div>
            <label className="flex items-start gap-3 text-sm ara-text-muted cursor-pointer">
              <input
                type="checkbox"
                checked={understood}
                onChange={(e) => setUnderstood(e.target.checked)}
                className="mt-1"
                data-testid="ara-understand-checkbox"
              />
              <span>
                I understand this passcode cannot be recovered. If I forget it, my
                Ara reflections cannot be read.
              </span>
            </label>
            <button
              type="submit"
              disabled={busy}
              className="ara-cta-primary w-full rounded-full px-6 py-3 font-medium disabled:opacity-50"
              data-testid="ara-create-lock-btn"
            >
              {busy ? 'Creating…' : 'Create Lock'}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
};

export default AraStart;
